import { AnalyticsService } from 'base/analytics';
import { getCreateProjectHref, getLogoutHref } from 'base/blocks_routes';
import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import {
  Button,
  ButtonPalette,
  ButtonTextareaContainer,
  SplitButtonContainer,
} from 'base/ui/button';
import {
  IconCode,
  IconCsv,
  IconFigma,
  IconGift,
  IconPricingCsv,
  IconReact,
  IconTxt,
  IconUnlock,
  IconWebflow,
} from 'base/ui/icons';
import { Inline } from 'base/ui/layout';
import { DropdownIcon, MenuButton } from 'base/ui/menu';
import {
  menuDivider,
  menuItem,
  MenuModel,
  menuText,
  subMenu,
} from 'base/ui/menu_model';

import { AccountsService } from 'base/accounts_service';
import { ActionOrigin } from 'base/analytics';
import { Callout, useCalloutState } from 'base/ui/callout';
import { mount } from 'base/ui/overlay';
import { ClickInTextarea } from 'base/ui/textarea';
import { ToastQueue } from 'base/ui/toast';
import { TextSmall, TextTiny } from 'base/ui/typography';
import { useToggle } from 'base/useToggle';
import {
  AccessLevel,
  BlocksService,
  SharingSettings,
} from 'blocks/blocks_service';
import { ConfirmMoveToUserDialog } from 'blocks/dashboard/projects_list/projects_list';
import { dashboardRouteToUrl } from 'blocks/dashboard/routes';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { KeyboardShortcutsDialog } from 'blocks/keyboard_shortcuts_dialog';
import { manageAccountRouteToUrl } from 'blocks/manage_account/routes';
import { isMac } from 'blocks/platform';
import { ReferralEarningDialog } from 'blocks/referral_earning';
import { SitemapImportDialog } from 'blocks/sitemap_import/sitemap_import_dialog';
import { UndoManager } from 'blocks/woot_woot/undo';
import classNames from 'classnames';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { getUserJwt } from '../../../library/src/modules/cookies';
import { shortcut, useKeyboardShortcuts } from '../keyboard_shortcuts';
import { CanvasViewport } from './canvas';
import { CommentingExport } from './commenting';
import { UseProjectOwner } from './editor';
import { EditorMode, editorModeStore } from './editor_mode';
import {
  ExportDialogName,
  exportFormat,
  exportFormatMenuItems,
  exportMenu,
  useExportDialogState,
} from './export_and_share';
import { flagsMenu } from './flags_menu';
import styles from './header.css';
import { Project } from './model';
import { walkProject } from './model_helpers';
import { OnboardingDialog } from './onboarding/dialog';
import { getPreferencesMenu } from './preferences_menu';
import { promptOverridesMenu } from './prompt_overrides';
import IconRelumeColor from './relume_color.react.svg';
import { SelectionStore } from './selection/store';
import {
  pageAskAiMenu,
  SelectionActions,
  selectionActionsMenu,
} from './selection_actions';
import { ProjectSession } from './session/session';
import { StyleGuideExportDialog } from './styleguide/export_dialog';
import { zoomMenu } from './zoom_and_breakpoint_controls';

export const Header = observer(function _Header({
  viewport,
  accountsService,
  project,
  selectionActions,
  selection,
  commenting,
  flags,
  toastQueue,
  session,
  blocksService,
  analytics,
  library,
  projectOwner,
  onSwitchMode,
}: {
  viewport: CanvasViewport;
  project: Project;
  accountsService: AccountsService;
  selectionActions: SelectionActions;
  selection: SelectionStore;
  commenting: CommentingExport;
  flags: FeatureFlagsStore;
  toastQueue: ToastQueue;
  session: ProjectSession;
  blocksService: BlocksService;
  analytics: AnalyticsService;
  library: LibraryCache;
  projectOwner: UseProjectOwner;
  onSwitchMode: (newMode: EditorMode) => void;
}) {
  const [startExport, currentExportDialog] = useExportDialogState(
    project,
    analytics,
    session,
    blocksService,
    library,
    toastQueue,
    selection,
    flags,
  );
  const [showDemoVideo, setShowDemoVideo] = React.useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] =
    React.useState(false);
  const [settings, setSettings] = React.useState<SharingSettings | undefined>();
  const [confirmingMoveToUser, setConfirmingMoveToUser] = React.useState(false);
  const [showImportSitemap, setShowImportSitemap] = React.useState(false);

  useModeSwitcherShortcuts(analytics, session);
  useKeyboardShortcuts(true, {
    [shortcut('?', { cmd: true, shift: true })]: () =>
      setShowKeyboardShortcuts(true),
  });

  const onStyleguide = editorModeStore.get() === EditorMode.Styleguide;
  const isLoggedIn = !!getUserJwt();

  React.useEffect(() => {
    if (isLoggedIn) {
      blocksService
        .projectGetSharingSettings(session.projectToken)
        .then(setSettings);
    }
  }, [blocksService, session, isLoggedIn]);

  const updateSettings = () => {
    blocksService
      .projectGetSharingSettings(session.projectToken)
      .then(setSettings);
  };

  const workspaceLabel = (name: string) => `${name} workspace`;
  const teamName = settings?.teamName || 'Team';
  const onMove = (msg: string) => {
    toastQueue.push({
      label: msg,
    });
    updateSettings();
    setConfirmingMoveToUser(false);
  };
  const onMoveError = () => {
    toastQueue.push({
      label: 'Error moving project',
    });
    updateSettings();
    setConfirmingMoveToUser(false);
  };
  const doMoveToUser = async () => {
    await blocksService
      .projectUpdateSharingSettings({
        projectToken: session.projectToken,
        ownershipType: 'user',
      })
      .catch((e) => onMoveError)
      .finally(() => onMove(`Project moved to ${workspaceLabel('personal')}`));
    analytics.track({
      event: 'project:move_button_click',
      button_location: 'share_dialog',
      move_to: 'user',
    });
  };
  const doMoveToTeam = (teamName: string) => {
    blocksService
      .projectUpdateSharingSettings({
        projectToken: session.projectToken,
        ownershipType: 'team',
      })
      .catch((e) => onMoveError)
      .finally(() => onMove(`Project moved to ${workspaceLabel(teamName)}`));
    analytics.track({
      event: 'project:move_button_click',
      button_location: 'share_dialog',
      move_to: 'team',
    });
  };
  const moveMenuItem = settings
    ? settings.ownershipType === 'team'
      ? menuItem({
          label: `Move to ${workspaceLabel('personal')}`,
          onClick: () => setConfirmingMoveToUser(true),
        })
      : settings.ownershipType === 'user'
      ? menuItem({
          label: `Move to ${workspaceLabel(teamName)}`,
          onClick: () => doMoveToTeam(teamName),
        })
      : null
    : null;

  const {
    toggleOn: openStyleGuideExportDialog,
    toggleOff: closeStyleGuideExportDialog,
    value: styleGuideExportDialogOpen,
  } = useToggle();
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const fileMenu: MenuModel = [
    menuItem({
      label: 'New project',
      href: getCreateProjectHref(),
    }),
    ...(session.saveMenuItem ? [session.saveMenuItem()] : []),
    session.duplicateMenuItem,
    session.deleteMenuItem,
    menuDivider(),
    menuItem({
      label: 'Import from URL',
      onClick: session.showAuthWall ?? (() => setShowImportSitemap(true)),
    }),
    subMenu({
      label: 'Export',
      disabled: session.accessLevel < AccessLevel.OWNER,
      menu: exportMenu(startExport, ActionOrigin.Header),
    }),
    menuDivider(),
    moveMenuItem,
    menuDivider(),
    menuItem({
      label: 'Share',
      onClick: () => {
        analytics.track({
          event: 'project:share_button_click',
          button_location: 'file_menu',
        });
        startExport('shareLink', ActionOrigin.Header);
      },
    }),
  ].filter(exists);

  let viewMenu: MenuModel = [
    ...makeModeSwitcherMenuItems(getModeOptions(session), analytics),
    menuDivider(),
    ...zoomMenu(viewport),
  ];

  // Commenting overlay is not shown on Styleguide page, so remove
  // the toggle from view menu.
  if (!onStyleguide) {
    viewMenu = [commenting.getViewMenuItem(), ...viewMenu];
  }
  const { visible: sgbExportCalloutVisible, dismiss: dismissExportCallout } =
    useCalloutState({ key: 'sgb-export-callout' });

  const editMenu: MenuModel = [
    menuItem({
      label: 'Undo',
      onClick: () => session.undo.undo(),
      disabled: !session.undo.canUndo,
      disabledReason: "There's nothing to undo",
      shortcut: '⌘Z',
    }),
    menuItem({
      label: 'Redo',
      onClick: () => session.undo.redo(),
      disabled: !session.undo.canRedo,
      disabledReason: "There's nothing to redo",
      shortcut: '⌘⇧Z',
    }),
    menuDivider(),
    ...selectionActionsMenu(
      selectionActions,
      pageAskAiMenu(selectionActions.editing),
    ),
  ];

  const helpMenu: MenuModel = [
    menuItem({
      label: 'Keyboard shortcuts',
      onClick: () => setShowKeyboardShortcuts(true),
      shortcut: '⌘⇧?',
    }),
    menuItem({
      label: 'Learn',
      href: 'https://www.relume.io/resources',
      target: '_blank',
    }),
    menuItem({
      label: 'Demo video',
      onClick: () => setShowDemoVideo(true),
    }),
    menuItem({
      label: 'Slack community',
      href: 'https://slack.relume.io/',
      target: '_blank',
    }),
    menuItem({
      label: 'Updates',
      href: 'https://www.relume.io/whats-new',
      target: '_blank',
    }),
    menuItem({
      label: 'Troubleshooting',
      href: 'https://www.relume.io/troubleshooting',
      target: '_blank',
    }),
    menuItem({
      label: 'Support',
      href: 'https://www.relume.io/support',
      target: '_blank',
    }),
    ...(isLoggedIn
      ? [
          menuDivider(),
          menuItem({
            label: 'Manage Account',
            href: manageAccountRouteToUrl({
              path: 'manage-account',
            }),
            target: '_blank',
          }),
          menuItem({
            label: 'Log out',
            href: getLogoutHref(),
          }),
        ]
      : []),
  ];

  const headerMenu = [
    menuItem({
      label: 'Go to dashboard',
      href: dashboardRouteToUrl({ path: 'dashboard' }),
    }),
    menuDivider(),
    subMenu({
      menu: fileMenu,
      label: 'File',
    }),
    subMenu({
      menu: editMenu,
      label: 'Edit',
    }),
    subMenu({
      menu: viewMenu,
      label: 'View',
    }),
    subMenu({
      menu: getPreferencesMenu(project),
      label: 'Preferences',
    }),
    commenting.getNotificationsMenuItem?.(),
    menuDivider(),
    subMenu({
      menu: helpMenu,
      label: 'Help & Account',
    }),
    ...(session.isRelumeStaff
      ? [
          menuDivider(),
          subMenu({ menu: flagsMenu(flags), label: 'Flags' }),
          ...(flags.get('enablePromptOverrides')
            ? [
                subMenu({
                  menu: promptOverridesMenu(),
                  label: 'Prompt overrides',
                }),
              ]
            : []),
        ]
      : []),
  ].filter(exists);

  const noTeamLogo =
    !projectOwner.loading && projectOwner.data && !projectOwner.data.team?.logo;

  const headerBrand =
    noTeamLogo || session.accessLevel >= AccessLevel.OWNER ? (
      <MenuButton {...BUTTON_VARIANT} menu={headerMenu}>
        <div className={styles.mainMenuButton}>
          <div className={styles.mainMenuIcon}>
            <IconRelumeColor />
          </div>
          <div className={styles.mainMenuDropdown}>
            <DropdownIcon />
          </div>
        </div>
      </MenuButton>
    ) : projectOwner.loading || projectOwner.error ? null : (
      projectOwner.data?.team?.logo && (
        <>
          <a href={isLoggedIn ? '/dashboard' : '/'}>
            <img
              src={projectOwner.data.team.logo.files[0].url}
              className={styles.brandingLogo}
            />
          </a>
          <div className={styles.verticalLine} />
        </>
      )
    );

  const projectName =
    session.accessLevel < AccessLevel.OWNER ? (
      <TextSmall>{project.name || ''}</TextSmall>
    ) : (
      <ProjectNameButton
        project={project}
        canEdit={session.accessLevel >= AccessLevel.OWNER}
      />
    );

  // Add extra space when only team logo and project name are displayed.
  const inlineSpacing =
    session.accessLevel < AccessLevel.OWNER && !noTeamLogo ? 16 : 8;

  return (
    <>
      <div className={styles.headerLeft}>
        <Inline spacing={inlineSpacing} yalign="center">
          {headerBrand}

          {projectName}

          {session.accessLevel >= AccessLevel.OWNER && (
            <div className={styles.hideOnMobile}>
              <ReferralButton
                accountsService={accountsService}
                analyticsService={analytics}
                toastQueue={toastQueue}
              />
            </div>
          )}
        </Inline>
      </div>

      <div className={classNames(styles.headerRight, styles.hideOnMobile)}>
        <Inline spacing={8} yalign="center">
          {session.headerRightStart}
          {!session.locked &&
            editorModeStore.get() !== EditorMode.Styleguide &&
            commenting.headerButton}
          {session.accessLevel >= AccessLevel.OWNER && (
            <Button
              {...BUTTON_VARIANT}
              label="Share"
              onClick={() => {
                analytics.track({
                  event: 'project:share_button_click',
                  button_location: 'header',
                });
                startExport('shareLink', ActionOrigin.Header);
              }}
            />
          )}
          {!session.locked && session.accessLevel >= AccessLevel.OWNER && (
            <div ref={anchorRef}>
              {editorModeStore.get() === EditorMode.Styleguide && (
                <Callout
                  anchorRef={anchorRef}
                  title="Style Guide export is now live!"
                  body="Export your Style Guide to Figma and Webflow and save hours of time."
                  visible={sgbExportCalloutVisible}
                  delay={0}
                  direction="vertical"
                >
                  <div className={styles.gotItContainer}>
                    <Button
                      label="Got it"
                      size="small"
                      background="black"
                      variant="secondary"
                      onClick={dismissExportCallout}
                    />
                  </div>
                </Callout>
              )}
              <ExportButton
                startExport={(name) => startExport(name, ActionOrigin.Header)}
                project={project}
              />
            </div>
          )}
          {session.locked && (
            <Button
              icon={IconUnlock}
              variant="primary"
              label="Unlock project"
              onClick={() => session.showUpgradeWall?.('unlock')}
              gradient
            />
          )}
          {!isLoggedIn && (
            <Button
              variant="secondary"
              label="Log in"
              onClick={session.showAuthWall}
            />
          )}
          {session.headerRightEnd}
        </Inline>
      </div>

      <div className={classNames(styles.headerRight, styles.forMobile)}>
        <Inline spacing={8} yalign="center">
          <ModeSwitcherDropdown
            analytics={analytics}
            options={getModeOptions(session)}
          />
          {editorModeStore.get() !== EditorMode.Styleguide
            ? commenting.headerButton
            : null}
        </Inline>
      </div>

      {/* Center goes last so that it goes on top.  (If there's too many avatars they should go under the mode switcher) */}
      <div className={classNames(styles.headerCenter, styles.hideOnMobile)}>
        <ModeSwitcherPalette
          analytics={analytics}
          options={getModeOptions(session)}
          onSwitchMode={onSwitchMode}
        />
      </div>

      {currentExportDialog}
      {showDemoVideo && (
        <OnboardingDialog
          onClose={() => setShowDemoVideo(false)}
          mute={false}
        />
      )}
      {showKeyboardShortcuts && (
        <KeyboardShortcutsDialog
          onClose={() => setShowKeyboardShortcuts(false)}
        />
      )}
      {showImportSitemap && (
        <SitemapImportDialog
          analytics={analytics}
          blocksService={blocksService}
          onClose={() => setShowImportSitemap(false)}
          importBehaviour={{ kind: 'import-into-new-project' }}
        />
      )}
      {confirmingMoveToUser && (
        <ConfirmMoveToUserDialog
          name={project.name || 'Untitled Project'}
          doMove={doMoveToUser}
          onCancel={() => setConfirmingMoveToUser(false)}
        />
      )}
      {styleGuideExportDialogOpen && (
        <StyleGuideExportDialog onClose={closeStyleGuideExportDialog} />
      )}
    </>
  );
});

const UNDO_TOASTS_GROUP = Symbol('undo toasts group');

export function useUndoShortcuts(undo: UndoManager, toastQueue: ToastQueue) {
  const maybeUndo = React.useCallback(() => {
    if (!undo.canUndo) {
      toastQueue.push({
        label: "There's nothing to undo",
        group: UNDO_TOASTS_GROUP,
      });
      return;
    }
    undo.undo();
  }, [undo, toastQueue]);
  const maybeRedo = React.useCallback(() => {
    if (!undo.canRedo) {
      toastQueue.push({
        label: "There's nothing to redo",
        group: UNDO_TOASTS_GROUP,
      });
      return;
    }
    undo.redo();
  }, [undo, toastQueue]);

  useKeyboardShortcuts(true, {
    [shortcut('z', { cmd: true })]: maybeUndo,
    [shortcut('z', { cmd: true, shift: true })]: maybeRedo,
  });
}

const ProjectNameButton = observer(function _ProjectNameButton({
  project,
  canEdit,
}: {
  project: Project;
  canEdit: boolean;
}) {
  return (
    <Button {...BUTTON_VARIANT}>
      <ButtonTextareaContainer>
        <TextSmall>
          <ClickInTextarea
            value={project.name || ''}
            onChange={canEdit ? (name) => (project.name = name) : undefined}
            placeholder="Untitled Project"
            variant="invisible"
            enableClickIn={true}
          />
        </TextSmall>
      </ButtonTextareaContainer>
    </Button>
  );
});

const ReferralButton = ({
  toastQueue,
  accountsService,
  analyticsService,
}: {
  toastQueue: ToastQueue;
  accountsService: AccountsService;
  analyticsService: AnalyticsService;
}) => (
  <Button
    {...BUTTON_VARIANT}
    onClick={() => {
      analyticsService.track({
        event: 'referral_flow:invite_and_earn_button_clicked',
        location: 'sitebuilder',
      });
      const close = mount(
        <ReferralEarningDialog
          analyticsService={analyticsService}
          toastQueue={toastQueue}
          onClose={() => close()}
          accountsService={accountsService}
        />,
      );
    }}
  >
    <div className={styles.referralButton}>
      <Inline yalign="center" spacing={8}>
        <IconGift width={16} height={16} color="black" />
        <TextSmall>Invite & earn</TextSmall>
      </Inline>
    </div>
  </Button>
);

type ModeOption = {
  value: EditorMode;
  label: string;
  tooltip: string;
  tooltipShortcut: string;
  betaLozenge?: boolean;
};

const getModeOptions = (session: ProjectSession) =>
  [
    {
      value: EditorMode.Sitemap,
      label: 'Sitemap',
      tooltip: 'View Sitemap',
      tooltipShortcut: '⌥S',
    },
    {
      value: EditorMode.Wireframe,
      label: 'Wireframe',
      tooltip: 'View Wireframe',
      tooltipShortcut: '⌥W',
    },
    {
      value: EditorMode.Styleguide,
      label: 'Style Guide',
      tooltip: 'View Style Guide',
      tooltipShortcut: '⌥G',
      betaLozenge: !session.flags.get('enableDesignView'),
    },
    ...(session.flags.get('enableDesignView')
      ? [
          {
            value: EditorMode.Design,
            label: 'Design',
            tooltip: 'View Design',
            tooltipShortcut: '⌥D',
          },
        ]
      : []),
  ] satisfies ModeOption[];

function makeChangeModeCallback(
  analytics: AnalyticsService,
  value: EditorMode,
) {
  return () => {
    if (editorModeStore.get() === value) return;
    editorModeStore.set(value);
    analytics.track({
      event: 'blocks/change-mode',
      mode: value,
    });
  };
}

function useModeSwitcherShortcuts(
  analytics: AnalyticsService,
  session: ProjectSession,
) {
  /**
   * In mac, alt + w = ∑ and alt + s = ß and alt + g = ©
   * */
  useKeyboardShortcuts(true, {
    [shortcut(isMac ? 'ß' : 's', { alt: true })]: makeChangeModeCallback(
      analytics,
      EditorMode.Sitemap,
    ),
    [shortcut(isMac ? '∑' : 'w', { alt: true })]: makeChangeModeCallback(
      analytics,
      EditorMode.Wireframe,
    ),
    [shortcut(isMac ? '©' : 'g', { alt: true })]: makeChangeModeCallback(
      analytics,
      EditorMode.Styleguide,
    ),
    ...(session.flags.get('enableDesignView') && {
      [shortcut(isMac ? '∂' : 'd', { alt: true })]: makeChangeModeCallback(
        analytics,
        EditorMode.Design,
      ),
    }),
  });
}

const LabelWithBetaLozenge = ({ children }: { children: string }) => {
  return (
    <div className={styles.labelWithBetaLozenge}>
      <TextSmall>{children}</TextSmall>
      <div className={styles.betaLozenge}>
        <TextTiny color="gradient">Beta</TextTiny>
      </div>
    </div>
  );
};

const ModeSwitcherPalette = observer(function _ModeSwitcherPalette({
  analytics,
  options,
  onSwitchMode = (newMode: EditorMode) => {},
}: {
  analytics: AnalyticsService;
  options: readonly ModeOption[];
  onSwitchMode: (newMode: EditorMode) => void;
}) {
  const onChange = (v: EditorMode) => {
    editorModeStore.set(v);
    analytics.track({
      event: 'blocks/change-mode',
      mode: v,
    });
    onSwitchMode(v);
  };
  const value = editorModeStore.get();

  return (
    <ButtonPalette isDarker isFullHeight>
      <div className={styles.modeSwitcherButtonGroup}>
        {options.map((option, i) => {
          return (
            <Button
              key={i}
              onClick={() => onChange(option.value)}
              variant={option.value === value ? 'secondary' : 'subtle'}
              subtlyDisabled={option.value === value}
              tooltip={option.value === value ? undefined : option.tooltip}
              tooltipShortcut={option.tooltipShortcut}
              tooltipPlacement="vertical"
              {...(option.betaLozenge
                ? {
                    children: (
                      <LabelWithBetaLozenge>
                        {option.label}
                      </LabelWithBetaLozenge>
                    ),
                  }
                : {
                    label: option.label,
                  })}
            />
          );
        })}
      </div>
    </ButtonPalette>
  );
});

function makeModeSwitcherMenuItems(
  options: readonly ModeOption[],
  analytics: AnalyticsService,
) {
  return options.map(({ value, label }) =>
    menuItem({
      label,
      onClick: makeChangeModeCallback(analytics, value),
      checked: editorModeStore.get() === value,
    }),
  );
}

const ModeSwitcherDropdown = observer(function _ModeSwitcherDropdown({
  analytics,
  options,
}: {
  options: readonly ModeOption[];
  analytics: AnalyticsService;
}) {
  const items = makeModeSwitcherMenuItems(options, analytics);
  return (
    <MenuButton
      {...BUTTON_VARIANT}
      label={items.find((i) => i.checked)?.label}
      iconRight={DropdownIcon}
      menu={items}
      search="hidden"
    />
  );
});

const ExportButton = observer(function ExportButton({
  startExport,
  project,
}: {
  startExport: (name: ExportDialogName) => void;
  project: Project;
}) {
  const menu = [
    menuText({ label: 'Export format', size: 'tiny' }),
    ...exportFormatMenuItems.get(),
    menuDivider(),
    menuItem({
      label: 'Export to TXT',
      onClick: () => startExport('txt'),
      icon: IconTxt,
    }),
    menuItem({
      label: 'Export to CSV',
      onClick: () => startExport('csv'),
      icon: IconCsv,
    }),
    menuItem({
      label: 'Export to Excel (with pricing)',
      onClick: () => startExport('pricingCsv'),
      icon: IconPricingCsv,
    }),
  ];
  let Icon: React.ComponentType;
  switch (exportFormat.value) {
    case 'figma':
      Icon = IconFigma;
      break;
    case 'webflow':
      Icon = IconWebflow;
      break;
    case 'react':
      Icon = IconReact;
      break;
    case 'html':
      Icon = IconCode;
      break;
  }
  const [reactExportReady, setReactExportReady] = React.useState(false);
  React.useEffect(() => {
    return when(
      () => {
        let ready = false;
        walkProject(project, (node) => {
          if (node.type === 'section') {
            if (node.node.element.current != null) {
              ready = true;
              return false;
            }
          }
        });
        return ready;
      },
      () => {
        setReactExportReady(true);
      },
    );
  }, [project]);
  const exportDisabled = exportFormat.isSourceCodeExport && !reactExportReady;
  return (
    <>
      <SplitButtonContainer>
        <MenuButton {...BUTTON_VARIANT} menu={menu} search="hidden">
          <div className={styles.splitButton}>
            <Icon />
            <DropdownIcon />
          </div>
        </MenuButton>
        <Button
          {...BUTTON_VARIANT}
          label="Export"
          disabled={exportDisabled}
          tooltip={
            exportDisabled
              ? 'You need to create wireframes to export'
              : undefined
          }
          onClick={() => startExport(exportFormat.value)}
        />
      </SplitButtonContainer>
    </>
  );
});

const BUTTON_VARIANT = { variant: 'secondary', background: 'grey3' } as const;
